/**
 * Caixa (plano, seção 11.1, função 7): total do dia e do mês, por forma de pagamento,
 * e o que falta receber.
 */
import type { Appointment, Payment, PaymentMethod } from '../schemas/entities';
import { amountDue } from './appointments';
import { type LocalDate, toMs, utcToZoned } from './time';

export const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  pix: 'Pix',
  cash: 'Dinheiro',
  card: 'Cartão',
};

export interface CashSummary {
  total: number;
  byMethod: Record<PaymentMethod, number>;
  count: number;
}

export interface Receivable {
  appointment: Appointment;
  due: number;
  paid: number;
  missing: number;
}

function emptyByMethod(): Record<PaymentMethod, number> {
  return { pix: 0, cash: 0, card: 0 };
}

/** Soma dos pagamentos com data local entre from e to (inclusive). */
export function cashSummary(payments: Payment[], from: LocalDate, to: LocalDate, timeZone: string): CashSummary {
  const byMethod = emptyByMethod();
  let total = 0;
  let count = 0;
  for (const p of payments) {
    if (p.deleted_at) continue;
    const date = utcToZoned(p.paid_at, timeZone).date;
    if (date < from || date > to) continue;
    byMethod[p.method] += p.amount_cents;
    total += p.amount_cents;
    count++;
  }
  return { total, byMethod, count };
}

/** Quanto já foi pago de cada atendimento. */
export function paidByAppointment(payments: Payment[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const p of payments) {
    if (p.deleted_at || !p.appointment_id) continue;
    out.set(p.appointment_id, (out.get(p.appointment_id) ?? 0) + p.amount_cents);
  }
  return out;
}

/** Atendimentos concluídos com valor em aberto ("a receber"). */
export function receivables(appointments: Appointment[], payments: Payment[]): Receivable[] {
  const paid = paidByAppointment(payments);
  const out: Receivable[] = [];
  for (const a of appointments) {
    if (a.deleted_at || a.status !== 'completed') continue;
    const due = amountDue(a);
    const p = paid.get(a.id) ?? 0;
    if (p < due) out.push({ appointment: a, due, paid: p, missing: due - p });
  }
  return out.sort((x, y) => toMs(y.appointment.start_utc) - toMs(x.appointment.start_utc));
}

export function totalReceivable(list: Receivable[]): number {
  return list.reduce((sum, r) => sum + r.missing, 0);
}
